import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  VideoCameraIcon,
  QuestionMarkCircleIcon,
  BookOpenIcon,
  AcademicCapIcon,
  PencilIcon,
  FlagIcon,
  ArrowPathIcon,
  PauseIcon,
  PlayIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Mock course data
const MOCK_COURSE = {
  id: '1',
  title: 'Khóa Học An Toàn Lao Động 101',
  description: 'Tìm hiểu các quy trình và quy định an toàn thiết yếu cho môi trường làm việc.',
  progress: 75,
  modules: [
    {
      id: 'm1',
      title: 'Giới Thiệu về An Toàn',
      lessons: [
        { id: 'l1', title: 'Tổng Quan về An Toàn Lao Động', type: 'video', duration: '10:00', completed: true },
        { id: 'l2', title: 'Quy Định và Tiêu Chuẩn An Toàn', type: 'document', duration: '15:00', completed: true },
        { id: 'l3', title: 'Bài Kiểm Tra Mô-đun', type: 'quiz', duration: '5:00', completed: true }
      ]
    },
    {
      id: 'm2',
      title: 'Nhận Diện Mối Nguy',
      lessons: [
        { id: 'l4', title: 'Các Mối Nguy Phổ Biến Tại Nơi Làm Việc', type: 'video', duration: '12:00', completed: true },
        { id: 'l5', title: 'Kỹ Thuật Đánh Giá Rủi Ro', type: 'interactive', duration: '20:00', completed: true },
        { id: 'l6', title: 'Quy Trình Báo Cáo Mối Nguy', type: 'document', duration: '8:00', completed: false },
        { id: 'l7', title: 'Bài Kiểm Tra Mô-đun', type: 'quiz', duration: '10:00', completed: false }
      ]
    },
    {
      id: 'm3',
      title: 'Quy Trình Khẩn Cấp',
      lessons: [
        { id: 'l8', title: 'Quy Trình An Toàn Cháy Nổ', type: 'video', duration: '15:00', completed: false },
        { id: 'l9', title: 'Quy Trình Sơ Tán', type: 'document', duration: '10:00', completed: false },
        { id: 'l10', title: 'Sơ Cứu Cơ Bản', type: 'video', duration: '18:00', completed: false },
        { id: 'l11', title: 'Bài Tập Ứng Phó Khẩn Cấp', type: 'interactive', duration: '25:00', completed: false },
        { id: 'l12', title: 'Đánh Giá Cuối Cùng', type: 'quiz', duration: '30:00', completed: false }
      ]
    }
  ]
};

// UI Component to show empty state when no lesson is selected
const NoLessonSelected = ({ t }) => (
  <div className="flex flex-col items-center justify-center h-full py-20">
    <DocumentTextIcon className="h-16 w-16 text-gray-300 mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-1">{t('learn.coursePlayer.selectLesson')}</h3>
    <p className="text-sm text-gray-500 text-center max-w-md">
      {t('learn.coursePlayer.selectLessonDescription')}
    </p>
  </div>
);

// UI Component to show when course is completed
const CourseCompleted = ({ t }) => (
  <div className="flex flex-col items-center justify-center h-full py-20">
    <div className="bg-green-100 p-3 rounded-full mb-4">
      <CheckCircleIcon className="h-16 w-16 text-green-500" />
    </div>
    <h3 className="text-xl font-medium text-gray-900 mb-2">{t('learn.coursePlayer.courseCompleted')}</h3>
    <p className="text-sm text-gray-500 text-center max-w-md mb-6">
      {t('learn.coursePlayer.courseCompletedDescription')}
    </p>
    <Link 
      to="/learn"
      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
    >
      {t('learn.coursePlayer.backToLearning')}
    </Link>
  </div>
);

// VideoLesson component to display video content
const VideoLesson = ({ t }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const progressInterval = useRef(null);

  // Simulate video playback
  const togglePlayback = () => {
    if (isPlaying) {
      clearInterval(progressInterval.current);
    } else {
      progressInterval.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval.current);
            setIsPlaying(false);
            return 100;
          }
          return prev + 0.5;
        });
      }, 100);
    }
    setIsPlaying(!isPlaying);
  };

  // Reset video progress
  const resetProgress = () => {
    clearInterval(progressInterval.current);
    setProgress(0);
    setIsPlaying(false);
  };

  return (
    <div 
      className="bg-gray-900 rounded-lg h-96 relative overflow-hidden"
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        <VideoCameraIcon className="h-24 w-24 text-gray-600" />
      </div>
      
      {/* Video progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-700">
        <div 
          className="h-full bg-red-600 transition-all duration-100"
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      
      {/* Video controls */}
      {showControls && (
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={togglePlayback}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white"
              >
                {isPlaying ? 
                  <PauseIcon className="h-5 w-5" /> : 
                  <PlayIcon className="h-5 w-5" />
                }
              </button>
              <span className="text-white text-sm">{Math.floor(progress / 100 * 600)}s / 10:00</span>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={resetProgress}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white"
              >
                <ArrowPathIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// DocumentLesson component to display reading content
const DocumentLesson = ({ lesson }) => (
  <div className="prose max-w-none">
    <h2>{lesson.title}</h2>
    <p>Chào mừng bạn đến với khóa học An toàn Lao động. Tài liệu này sẽ cung cấp thông tin quan trọng về các quy định và tiêu chuẩn an toàn tại nơi làm việc. Việc tuân thủ các quy định này là điều bắt buộc để đảm bảo môi trường làm việc an toàn cho tất cả mọi người.</p>
    <p>An toàn lao động không chỉ là trách nhiệm của người quản lý mà còn là trách nhiệm của mỗi cá nhân. Mỗi nhân viên cần nắm rõ các quy trình an toàn, biết cách nhận diện mối nguy và có khả năng ứng phó trong tình huống khẩn cấp.</p>
    <h3>Điểm Chính</h3>
    <ul>
      <li>Tuân thủ các quy định an toàn là trách nhiệm của mỗi người.</li>
      <li>Thường xuyên kiểm tra thiết bị trước khi sử dụng.</li>
      <li>Báo cáo ngay lập tức khi phát hiện mối nguy hiểm.</li>
      <li>Tham gia đầy đủ các buổi đào tạo an toàn định kỳ.</li>
    </ul>
  </div>
);

// QuizLesson component to display quiz content
const QuizLesson = ({ lesson, t }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-6">
    <h2 className="text-xl font-medium text-gray-900 mb-4">{lesson.title}</h2>
    <p className="text-gray-500 mb-6">{t('learn.coursePlayer.quizInstructions')}</p>
    <div className="space-y-6">
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-2">Câu hỏi 1</h3>
        <p className="mb-3">Bước đầu tiên trong quy trình ứng phó khẩn cấp là gì?</p>
        <div className="space-y-2">
          <div className="flex items-center">
            <input type="radio" name="q1" id="q1a" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500" />
            <label htmlFor="q1a" className="ml-3 text-gray-700">Đánh giá tình huống</label>
          </div>
          <div className="flex items-center">
            <input type="radio" name="q1" id="q1b" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500" />
            <label htmlFor="q1b" className="ml-3 text-gray-700">Gọi trợ giúp</label>
          </div>
          <div className="flex items-center">
            <input type="radio" name="q1" id="q1c" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500" />
            <label htmlFor="q1c" className="ml-3 text-gray-700">Sơ tán khu vực</label>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 p-4 rounded-lg">
        <h3 className="font-medium text-gray-900 mb-2">Câu hỏi 2</h3>
        <p className="mb-3">Trong số những điều sau đây, điều nào KHÔNG phải là mối nguy phổ biến tại nơi làm việc?</p>
        <div className="space-y-2">
          <div className="flex items-center">
            <input type="radio" name="q2" id="q2a" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500" />
            <label htmlFor="q2a" className="ml-3 text-gray-700">Thiết bị điện</label>
          </div>
          <div className="flex items-center">
            <input type="radio" name="q2" id="q2b" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500" />
            <label htmlFor="q2b" className="ml-3 text-gray-700">Cây cảnh văn phòng</label>
          </div>
          <div className="flex items-center">
            <input type="radio" name="q2" id="q2c" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500" />
            <label htmlFor="q2c" className="ml-3 text-gray-700">Tràn đổ hóa chất</label>
          </div>
        </div>
      </div>
    </div>
    <div className="mt-6">
      <button 
        className="w-full px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        {t('learn.coursePlayer.submitQuiz')}
      </button>
    </div>
  </div>
);

// InteractiveLesson component to display interactive content
const InteractiveLesson = ({ lesson, t }) => (
  <div className="bg-white border border-gray-200 rounded-lg p-6 text-center">
    <h2 className="text-xl font-medium text-gray-900 mb-4">{t('learn.coursePlayer.interactiveExercise')}</h2>
    <div className="bg-gray-50 p-8 rounded-lg mb-6">
      <BookOpenIcon className="h-16 w-16 text-indigo-500 mx-auto mb-4" />
      <p className="text-gray-500 mb-4">{t('learn.coursePlayer.interactivePlaceholder')}</p>
      <button 
        className="px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        {t('learn.coursePlayer.startExercise')}
      </button>
    </div>
  </div>
);

// New component for note taking
const NoteTakingPanel = ({ lessonId }) => {
  const { t } = useLanguage();
  const [notes, setNotes] = useState('');
  const [savedNotes, setSavedNotes] = useState({});
  const [isOpen, setIsOpen] = useState(false);

  // Load saved notes for this lesson
  useEffect(() => {
    const savedNotesData = localStorage.getItem('courseNotes');
    if (savedNotesData) {
      const parsedNotes = JSON.parse(savedNotesData);
      setSavedNotes(parsedNotes);
      if (parsedNotes[lessonId]) {
        setNotes(parsedNotes[lessonId]);
      } else {
        setNotes('');
      }
    }
  }, [lessonId]);

  const saveNotes = () => {
    const updatedNotes = {
      ...savedNotes,
      [lessonId]: notes
    };
    localStorage.setItem('courseNotes', JSON.stringify(updatedNotes));
    setSavedNotes(updatedNotes);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 p-3 rounded-full bg-indigo-600 text-white shadow-lg hover:bg-indigo-700"
      >
        <PencilIcon className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden">
      <div className="flex justify-between items-center p-3 border-b">
        <h3 className="font-medium">Ghi chú bài học</h3>
        <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-gray-500">
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        className="w-full p-3 h-40 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
        placeholder="Viết ghi chú của bạn tại đây..."
      ></textarea>
      <div className="p-3 border-t bg-gray-50 flex justify-end">
        <button
          onClick={saveNotes}
          className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
        >
          Lưu ghi chú
        </button>
      </div>
    </div>
  );
};

const CoursePlayer = () => {
  const { courseId, lessonId } = useParams();
  const { t, locale } = useLanguage();
  const [course, setCourse] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modulesOpen, setModulesOpen] = useState({});
  const [completionStatus, setCompletionStatus] = useState({});
  const [showLessonProgress, setShowLessonProgress] = useState(false);
  const [reportIssueMode, setReportIssueMode] = useState(false);
  const [issueText, setIssueText] = useState('');

  // Fetch course data
  useEffect(() => {
    // In a real app, this would be an API call
    setTimeout(() => {
      setCourse(MOCK_COURSE);
      setLoading(false);
      
      // Initialize open modules
      const initialModuleState = {};
      MOCK_COURSE.modules.forEach(module => {
        initialModuleState[module.id] = true; // Default all modules to open
      });
      setModulesOpen(initialModuleState);
      
      // Initialize completion status
      const initialCompletionStatus = {};
      MOCK_COURSE.modules.forEach(module => {
        module.lessons.forEach(lesson => {
          initialCompletionStatus[lesson.id] = lesson.completed;
        });
      });
      setCompletionStatus(initialCompletionStatus);
      
    }, 800);
  }, [courseId]);

  // Set current lesson (either from URL or first incomplete lesson)
  useEffect(() => {
    if (!course) return;
    
    if (lessonId) {
      // Find the specified lesson
      let foundLesson = null;
      course.modules.forEach(module => {
        const lesson = module.lessons.find(l => l.id === lessonId);
        if (lesson) foundLesson = lesson;
      });
      
      if (foundLesson) {
        setCurrentLesson(foundLesson);
        return;
      }
    }
    
    // If no lesson specified or not found, select first incomplete lesson
    let defaultLesson = null;
    for (const module of course.modules) {
      const firstIncompleteLesson = module.lessons.find(lesson => !lesson.completed);
      if (firstIncompleteLesson) {
        defaultLesson = firstIncompleteLesson;
        break;
      }
    }
    
    // If all lessons are complete, select the first lesson
    if (!defaultLesson && course.modules.length > 0 && course.modules[0].lessons.length > 0) {
      defaultLesson = course.modules[0].lessons[0];
    }
    
    setCurrentLesson(defaultLesson);
  }, [course, lessonId]);

  const toggleModule = (moduleId) => {
    setModulesOpen(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const markLessonComplete = () => {
    if (!currentLesson) return;
    
    setCompletionStatus(prev => ({
      ...prev,
      [currentLesson.id]: true
    }));
  };

  const getNextLesson = () => {
    if (!course || !currentLesson) return null;
    
    let foundCurrent = false;
    let nextLesson = null;
    
    for (const module of course.modules) {
      for (const lesson of module.lessons) {
        if (foundCurrent) {
          nextLesson = lesson;
          return nextLesson;
        }
        if (lesson.id === currentLesson.id) {
          foundCurrent = true;
        }
      }
    }
    
    return null;
  };

  const getPreviousLesson = () => {
    if (!course || !currentLesson) return null;
    
    let previousLesson = null;
    
    for (const module of course.modules) {
      for (const lesson of module.lessons) {
        if (lesson.id === currentLesson.id) {
          return previousLesson;
        }
        previousLesson = lesson;
      }
    }
    
    return null;
  };

  const nextLesson = getNextLesson();
  const previousLesson = getPreviousLesson();

  const getLessonIcon = (type) => {
    switch (type) {
      case 'video':
        return <VideoCameraIcon className="h-5 w-5" />;
      case 'document':
        return <DocumentTextIcon className="h-5 w-5" />;
      case 'quiz':
        return <QuestionMarkCircleIcon className="h-5 w-5" />;
      case 'interactive':
        return <BookOpenIcon className="h-5 w-5" />;
      default:
        return <DocumentTextIcon className="h-5 w-5" />;
    }
  };

  const getLessonTypeLabel = (type) => {
    return t(`learn.coursePlayer.types.${type}`);
  };

  // Report an issue with the content
  const reportIssue = () => {
    if (issueText.trim()) {
      // In a real app, this would send to an API
      console.log(`Issue reported for lesson ${currentLesson.id}: ${issueText}`);
      setIssueText('');
      setReportIssueMode(false);
      // Show success message
      toast.success('Cảm ơn bạn đã báo cáo vấn đề! Chúng tôi sẽ xem xét sớm nhất có thể.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="text-center py-12">
        <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h2 className="text-lg font-medium text-gray-900 mb-1">{t('learn.coursePlayer.courseNotFound')}</h2>
        <p className="text-gray-500 mb-6">{t('learn.coursePlayer.courseNotFoundDescription')}</p>
        <Link 
          to="/learn"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {t('learn.coursePlayer.backToLearning')}
        </Link>
      </div>
    );
  }

  // Calculate total and completed lessons
  const totalLessons = course.modules.reduce((total, module) => total + module.lessons.length, 0);
  const completedLessons = Object.values(completionStatus).filter(Boolean).length;

  // Check if all lessons are completed
  const isFullyCompleted = completedLessons === totalLessons;

  return (
    <div className="flex flex-col lg:flex-row">
      {/* Course sidebar */}
      <div className="w-full lg:w-1/4 xl:w-1/5 bg-white border-r border-gray-200 px-4 py-6">
        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900 mb-2">{course.title}</h1>
          <div className="flex items-center text-sm text-gray-500 mb-4">
            <div className="flex items-center mr-4">
              <ClockIcon className="h-4 w-4 mr-1" />
              <span>10 {t('learn.coursePlayer.estimatedTime')}</span>
            </div>
            <div className="flex items-center">
              <CheckCircleIcon className="h-4 w-4 mr-1" />
              <span>{completedLessons}/{totalLessons} {t('learn.coursePlayer.complete')}</span>
            </div>
          </div>
          <div 
            className="mb-2 cursor-pointer" 
            onClick={() => setShowLessonProgress(!showLessonProgress)}
          >
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-indigo-600 h-2 rounded-full transition-all duration-500"
                style={{ width: `${Math.round((completedLessons / totalLessons) * 100)}%` }}
              ></div>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            {Math.round((completedLessons / totalLessons) * 100)}% {t('learn.coursePlayer.progress')}
          </p>
          
          {showLessonProgress && (
            <div className="mt-4 p-3 bg-indigo-50 rounded-md text-xs">
              <h3 className="font-medium text-indigo-700 mb-2">Chi tiết tiến độ</h3>
              <div className="space-y-2">
                {course.modules.map(module => (
                  <div key={module.id}>
                    <div className="font-medium text-gray-700">{module.title}</div>
                    <div className="ml-2">
                      {module.lessons.map(lesson => (
                        <div key={lesson.id} className="flex items-center justify-between">
                          <span className="truncate">{lesson.title.slice(0, 20)}...</span>
                          {completionStatus[lesson.id] ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <div className="h-3 w-3 rounded-full border border-gray-300"></div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mb-2 pb-2 border-b border-gray-200">
          <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {t('play.courseContent')}
          </h2>
        </div>

        <div className="space-y-2">
          {course.modules.map(module => (
            <div key={module.id} className="border border-gray-100 rounded-md overflow-hidden">
              <button
                className="flex justify-between items-center w-full px-4 py-3 bg-gray-50 text-left"
                onClick={() => toggleModule(module.id)}
              >
                <span className="font-medium text-gray-900">{module.title}</span>
                <ChevronRightIcon className={`h-5 w-5 text-gray-400 transition-transform ${modulesOpen[module.id] ? 'transform rotate-90' : ''}`} />
              </button>
              
              {modulesOpen[module.id] && (
                <div className="px-2 py-2 bg-white">
                  {module.lessons.map((lesson) => (
                    <button
                      key={lesson.id}
                      className={`flex items-center w-full px-2 py-2 text-left text-sm rounded-md transition ${
                        currentLesson && currentLesson.id === lesson.id
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => setCurrentLesson(lesson)}
                    >
                      <div className="mr-3 flex-shrink-0">
                        {completionStatus[lesson.id] ? (
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center">
                            <CheckCircleIcon className="h-4 w-4 text-green-600" />
                          </div>
                        ) : (
                          <div className="h-5 w-5 rounded-full border border-gray-300 flex items-center justify-center">
                            {getLessonIcon(lesson.type)}
                          </div>
                        )}
                      </div>
                      <span className="flex-1 truncate">{lesson.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 px-4 lg:px-8 py-6">
        {currentLesson ? (
          <div>
            <div className="flex flex-wrap justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2 lg:mb-0">{currentLesson.title}</h2>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center text-gray-500">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  <span>{currentLesson.duration}</span>
                </div>
                <div className="flex items-center text-gray-500">
                  <DocumentTextIcon className="h-4 w-4 mr-1" />
                  <span>{getLessonTypeLabel(currentLesson.type)}</span>
                </div>
                {completionStatus[currentLesson.id] ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    <span>{t('learn.coursePlayer.completed')}</span>
                  </div>
                ) : (
                  <button
                    onClick={markLessonComplete}
                    className="text-indigo-600 hover:text-indigo-800 flex items-center"
                  >
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    <span>{t('learn.coursePlayer.markComplete')}</span>
                  </button>
                )}
                <button
                  onClick={() => setReportIssueMode(!reportIssueMode)}
                  className="text-gray-500 hover:text-gray-700 flex items-center"
                >
                  <FlagIcon className="h-4 w-4 mr-1" />
                  <span>Báo cáo vấn đề</span>
                </button>
              </div>
            </div>

            {reportIssueMode && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                <h3 className="font-medium text-orange-800 mb-2">Báo cáo vấn đề với nội dung này</h3>
                <textarea
                  value={issueText}
                  onChange={(e) => setIssueText(e.target.value)}
                  className="w-full p-2 border border-orange-300 rounded-md text-sm mb-2"
                  placeholder="Mô tả vấn đề bạn gặp phải..."
                  rows={3}
                ></textarea>
                <div className="flex justify-end space-x-2">
                  <button 
                    onClick={() => setReportIssueMode(false)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Hủy
                  </button>
                  <button 
                    onClick={reportIssue}
                    className="px-3 py-1 text-sm bg-orange-600 text-white rounded-md hover:bg-orange-700"
                  >
                    Gửi báo cáo
                  </button>
                </div>
              </div>
            )}

            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              {currentLesson.type === 'video' && <VideoLesson t={t} />}
              {currentLesson.type === 'document' && <DocumentLesson lesson={currentLesson} />}
              {currentLesson.type === 'quiz' && <QuizLesson lesson={currentLesson} t={t} />}
              {currentLesson.type === 'interactive' && <InteractiveLesson lesson={currentLesson} t={t} />}
            </div>

            <div className="flex justify-between items-center mt-8">
              {previousLesson ? (
                <Link 
                  to={`/learn/course/${courseId}/lesson/${previousLesson.id}`}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <ChevronLeftIcon className="h-5 w-5 mr-1" />
                  {t('learn.coursePlayer.previous')}
                </Link>
              ) : (
                <div></div>
              )}
              
              {nextLesson ? (
                <Link 
                  to={`/learn/course/${courseId}/lesson/${nextLesson.id}`}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {t('learn.coursePlayer.next')}
                  <ChevronRightIcon className="h-5 w-5 ml-1" />
                </Link>
              ) : (
                <button 
                  onClick={markLessonComplete}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  {t('learn.coursePlayer.finish')}
                  <CheckCircleIcon className="h-5 w-5 ml-1" />
                </button>
              )}
            </div>
          </div>
        ) : isFullyCompleted ? (
          <CourseCompleted t={t} />
        ) : (
          <NoLessonSelected t={t} />
        )}
      </div>
      
      {/* Note taking panel */}
      {currentLesson && <NoteTakingPanel lessonId={currentLesson.id} />}
    </div>
  );
};

export default CoursePlayer; 